// 加密云快照的自动每日调度（桌面端 E2EE 用户的异地备份兜底）。
//
// 触发条件（全部满足才跑）：
// - 桌面端（Electron）且已登录（有 token）
// - 云端账号已绑定
// - 本会话已输入过同步口令（E2EE 解锁态；口令只存内存，见 syncSession.ts）
// - 距上次自动快照尝试 > 24h（localStorage 持久化时间戳，多标签页共享防重复）
//
// 失败处理：全程静默（不弹 toast、不打扰用户），时间戳在尝试开始时就写入，
// 失败后下一天（>24h）才会再试——同时避免限流/网络抖动下的重试风暴。

import { getToken } from '@/api/auth';
import { cloudApi, cloudSyncApi } from '@/api/cloud';
import { settingsApi } from '@/api/settings';
import { syncAgentApi } from '@/api/syncAgent';
import { encryptBackup } from '@/services/syncCrypto';
import {
  getSyncSessionPassword, getSyncSessionSalt, setSyncSessionSalt,
} from '@/services/syncSession';

const isDesktop = !!window.psbDesktop?.isDesktop;

const LAST_AUTO_SNAPSHOT_KEY = 'psb:last-auto-snapshot-at';
const AUTO_SNAPSHOT_INTERVAL_MS = 24 * 60 * 60 * 1000;
export const DEFAULT_SNAPSHOT_EXPIRES_DAYS = 365;

let _autoSnapshotInFlight = false;

export interface SnapshotPushResult {
  entityCount: number;
  ciphertextBytes: number;
}

/** 读用户配置的快照保留天数（sync.snapshot_expires_days），缺省 365。 */
export const getSnapshotExpiresDays = async (): Promise<number> => {
  try {
    const res = await settingsApi.getSettings();
    const days = (res.data.sync as Record<string, unknown> | undefined)?.snapshot_expires_days;
    return days === 90 || days === 365 ? days : DEFAULT_SNAPSHOT_EXPIRES_DAYS;
  } catch {
    return DEFAULT_SNAPSHOT_EXPIRES_DAYS;
  }
};

/** 现有加密快照管线：本机导出 → 确保 salt → 客户端加密 → 密文上传。 */
export const runEncryptedSnapshotPipeline = async (
  password: string,
  expiresDays: number,
): Promise<SnapshotPushResult> => {
  // 1. 从本机后端拉全量数据（明文，本机 → 浏览器，本地链路不加密）
  const { data: exportData } = await (await import('@/api/client')).default.post('/api/v1/users/me/export');

  // 2. 确保有 salt（首次会生成）
  let currentSalt = getSyncSessionSalt();
  if (!currentSalt) {
    const res = await cloudSyncApi.initSync();
    currentSalt = res.salt;
    setSyncSessionSalt(res.salt);
  }

  // 3. 客户端加密（PBKDF2 派生 key + AES-GCM）
  const encrypted = await encryptBackup(exportData, password, currentSalt);

  // 4. 密文上传到云端（云端只存密文，无法解密）
  const stats = { notes: 0, clips: 0, knowledge: 0, capsules: 0 };
  const data = exportData?.data || {};
  for (const k of Object.keys(data)) {
    if (Array.isArray(data[k])) stats[k as keyof typeof stats] = data[k].length;
  }
  const entityCount = Object.values(stats).reduce((a, b) => a + b, 0);

  // 快照覆盖到的 op 锚点：带本机 op_cursor 上传，恢复端据此续拉增量 ops。
  // 拿不到（agent 未启用等）就不传——后端对缺失 op_seq 保持恢复前游标不动
  let opSeq: number | undefined;
  try {
    opSeq = (await syncAgentApi.status()).op_cursor;
  } catch {
    opSeq = undefined;
  }

  await cloudSyncApi.pushEncrypted({
    salt: encrypted.salt,
    iv: encrypted.iv,
    ciphertext: encrypted.ciphertext,
    entity_count: entityCount,
    expires_days: expiresDays,
    op_seq: opSeq,
  });

  return { entityCount, ciphertextBytes: encrypted.ciphertext.length };
};

const readLastAutoSnapshotAt = (): number => {
  try {
    return Number(localStorage.getItem(LAST_AUTO_SNAPSHOT_KEY)) || 0;
  } catch {
    return 0;
  }
};

const writeLastAutoSnapshotAt = (ts: number): void => {
  try {
    localStorage.setItem(LAST_AUTO_SNAPSHOT_KEY, String(ts));
  } catch { /* ignore */ }
};

/** 到点则静默跑一次自动快照；不满足条件或失败都安静返回。 */
export const maybeRunAutoSnapshot = async (): Promise<void> => {
  if (!isDesktop || _autoSnapshotInFlight) return;
  if (!getToken()) return;
  const password = getSyncSessionPassword();
  if (!password) return;
  if (Date.now() - readLastAutoSnapshotAt() < AUTO_SNAPSHOT_INTERVAL_MS) return;

  try {
    const status = await cloudApi.getStatus();
    if (!status?.bound) return;
  } catch {
    return;
  }

  // 时间戳在尝试前写入：多标签页/多次挂载只会有一个真正执行，
  // 失败后也要等下一个 24h 窗口再试（不打扰、不撞限流）
  _autoSnapshotInFlight = true;
  writeLastAutoSnapshotAt(Date.now());
  try {
    const expiresDays = await getSnapshotExpiresDays();
    await runEncryptedSnapshotPipeline(password, expiresDays);
  } catch {
    // 静默：下一天再试
  } finally {
    _autoSnapshotInFlight = false;
  }
};
